import { faListAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import KeyContext from 'CMFW/context/KeyContext';
import ModuleContext from 'CMFW/context/ModuleContext';
import TextInput from 'CMFW/input/TextInput';
import { AudioPlayer } from 'CMFW/layout/AudioPlayer';
import { EditorRow } from 'components/common/styles';
import InflectViewModal from 'components/inflect/InflectViewModal';
import VocGenderSelect from 'components/selects/VocGenderSelect';
import VocRefLevelSelect from 'components/selects/VocRefLevelSelect';
import { IVocDialect } from 'models/VocabularyModel';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import SwitchInput from '../../CMFW/input/SwitchInput';
import AsyncTaskWrapper from '../../CMFW/input/AsyncTaskWrapper';

const Dialect: React.FC = () => {
  const [showModal, setShowModal] = useState(false);

  const parentKey = useContext(KeyContext);

  const module = useContext(ModuleContext);
  const moduleItemValue: IVocDialect = module.getItemUpdatedValue(parentKey);

  const handleCloseShowInflects = () => {
    setShowModal(false);
  };

  const handleInflectClick = () => {
    setShowModal(true);
  };

  // Source inflect viewer removed

  return (
    <Wrapper>
      <EditorRowStyled>
        <TextInputExtra
          keyName={'source'}
          disabled={!moduleItemValue.sourceEditable}
          $dimmed={moduleItemValue.sourceShaded}
          $hidden={false}
        />
        <AsyncTaskWrapper task={moduleItemValue.nInflects} keyValue={'nInflects'}>
          <InflectIcon
            $dimmed={moduleItemValue.inflectShaded}
            $hidden={moduleItemValue.inflectHidden}
            $off={moduleItemValue.nInflects.value === 0}
            icon={faListAlt}
            onClick={handleInflectClick}
          />
        </AsyncTaskWrapper>
        <TargetGroup>
          <TextInputExtra
            keyName={'target'}
            $dimmed={moduleItemValue.targetShaded}
            $hidden={moduleItemValue.targetHidden}
          />
          {!moduleItemValue.transcriptionHidden && (
            <TextInputExtra
              keyName={'transcription'}
              placeholder={'pinyin'}
              $dimmed={moduleItemValue.targetShaded}
              $hidden={false}
            />
          )}
          {!moduleItemValue.refLevelHidden && (
            <VocRefLevelSelectStyled dialectId={moduleItemValue.dialectId} $dimmed={moduleItemValue.targetShaded} />
          )}
        </TargetGroup>
        <VocGenderSelectStyled
          dialectId={moduleItemValue.dialectId}
          $dimmed={moduleItemValue.genderShaded}
          $hidden={moduleItemValue.genderHidden}
        />
        <AudioPlayerStyled
          key={`audio-${moduleItemValue.vocId}-${moduleItemValue.dialectId}`}
          $dimmed={moduleItemValue.audioShaded}
          $hidden={moduleItemValue.audioHidden}
        />
        <SwitchInputStyled keyName={'targetEnabled'} disabled={!moduleItemValue.targetCanBeDisabled} />
        {showModal && <InflectViewModal onClose={handleCloseShowInflects} />}
      </EditorRowStyled>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-grow: 1;
`;

const EditorRowStyled = styled(EditorRow)`
  width: 100%;
  max-width: 700px;
  min-width: 0;
  & > div {
    margin: 0 4px;
  }
  & > div:first-child {
    margin-left: 6px;
  }
`;

const TextInputExtra = styled(TextInput)<{ $dimmed: boolean; $hidden: boolean }>`
  visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
  opacity: ${(props) => (props.$dimmed ? 0.5 : 1)};
  margin: 0 1px;
  min-width: 0;
`;

const TargetGroup = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 2px;
`;

const VocRefLevelSelectStyled = styled(VocRefLevelSelect)<{ $dimmed: boolean }>`
  opacity: ${(props) => (props.$dimmed ? 0.5 : 1)};
  margin: 0 1px;
`;

const VocGenderSelectStyled = styled(VocGenderSelect)<{ $dimmed: boolean; $hidden: boolean }>`
  visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
  opacity: ${(props) => (props.$dimmed ? 0.5 : 1)};
  margin: 0 4px 0 0;
`;

const AudioPlayerStyled = styled(AudioPlayer)<{ $dimmed: boolean; $hidden: boolean }>`
  && {
    visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
    opacity: ${(props) => (props.$dimmed ? 0.5 : 1)};
    margin: 0 10px;
  }
`;

const SwitchInputStyled = styled(SwitchInput)`
  && {
    margin: 0 4px;
  }
`;

const InflectIcon = styled(FontAwesomeIcon)<{ $dimmed: boolean; $hidden: boolean; $off: boolean }>`
  && {
    visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
    cursor: ${(props) => (props.$hidden ? 'default' : 'pointer')};
    opacity: ${(props) => (props.$dimmed ? 0.5 : 1)};
    color: ${(props) => (props.$off ? 'red' : 'black')};
  }
  padding: 4px;
`;

export default Dialect;
